import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, ExternalLink, Filter } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useStationsQuery } from "@/api/query-hooks";
import type { StationRecord } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { EmptyState, ErrorState, LoadingState } from "@/components/layout/route-states";
import { ClientScopePicker, useTenantClientScope } from "@/features/licensing/client-scope";
import { ClientIdentity } from "@/features/licensing/client-identity";
import { formatDateTime, isOlderThanHours, relativeDateTime } from "@/lib/date";
import { positiveIntegerSearch } from "@/lib/search";

type StationSearch = {
  appId?: string;
  clientId?: number;
  status?: string;
  activeOnly?: boolean;
  outdatedOnly?: boolean;
  sort?: string;
  page?: string;
  pageSize?: string;
};

export function StationsPage() {
  const { t, i18n } = useTranslation(["stations", "common"]);
  const navigate = useNavigate({ from: "/stations" });
  const search = useSearch({ from: "/stations" });
  const params = useMemo(() => stationParams(search), [search]);
  const scope = useTenantClientScope(search.clientId);
  const query = useStationsQuery(params, scope.ready);
  const page = pageFromSearch(search.page);
  const pageSize = pageSizeFromSearch(search.pageSize);

  if (scope.isLoading || (scope.ready && query.isLoading)) {
    return <LoadingState />;
  }
  if (scope.isError) {
    return <ErrorState title={t("common:states.error")} detail={scope.error instanceof Error ? scope.error.message : undefined} onRetry={() => void scope.refetch()} />;
  }
  if (scope.ready && query.isError) {
    return <ErrorState title={t("common:states.error")} detail={query.error.message} onRetry={() => void query.refetch()} />;
  }

  const stations = query.data?.items ?? [];

  return (
    <div className="grid gap-4">
      <div>
        <h2 className="text-lg font-semibold">{t("title")}</h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t("description")}</p>
      </div>
      {scope.tenant ? (
        <section className="rounded-md border bg-panel p-3">
          <ClientScopePicker
            value={search.clientId}
            required={scope.required}
            onChange={(clientId) => void navigate({ search: { ...search, clientId, page: undefined } })}
          />
        </section>
      ) : null}
      <section className="rounded-md border bg-panel p-3">
        <div className="grid gap-3 lg:grid-cols-[1fr_150px_auto_auto_260px]">
          <Field label={t("app")}>
            <Input
              value={search.appId ?? ""}
              placeholder="mailarchiver"
              onChange={(event) => void navigate({ search: { ...search, appId: event.target.value || undefined, page: undefined } })}
            />
          </Field>
          {!scope.tenant ? <Field label={t("client")}><Input value={search.clientId ?? ""} inputMode="numeric" onChange={(event) => void navigate({ search: { ...search, clientId: positiveIntegerSearch(event.target.value), page: undefined } })} /></Field> : null}
          <Field label={t("status")}>
            <Input
              value={search.status ?? ""}
              placeholder="HEALTHY"
              onChange={(event) => void navigate({ search: { ...search, status: event.target.value || undefined, page: undefined } })}
            />
          </Field>
          <div className="flex items-end gap-2">
            <Switch
              checked={search.activeOnly !== false}
              onCheckedChange={(checked) => void navigate({ search: { ...search, activeOnly: checked, page: undefined } })}
            />
            <span className="pb-2 text-sm">{t("activeOnly")}</span>
          </div>
          <div className="flex items-end gap-2">
            <Switch
              checked={search.outdatedOnly === true}
              onCheckedChange={(checked) => void navigate({ search: { ...search, outdatedOnly: checked || undefined, page: undefined } })}
            />
            <span className="pb-2 text-sm">{t("outdatedOnly")}</span>
          </div>
          <Field label={t("sort")}>
            <div className="grid grid-cols-2 gap-1">
              {(["last_seen_desc", "enrolled_desc", "version_asc", "version_desc"] as const).map((sort) => (
                <Button
                  key={sort}
                  type="button"
                  variant={(search.sort ?? "last_seen_desc") === sort ? "default" : "outline"}
                  onClick={() => void navigate({ search: { ...search, sort: sort === "last_seen_desc" ? undefined : sort, page: undefined } })}
                >
                  {t(sort)}
                </Button>
              ))}
            </div>
          </Field>
        </div>
      </section>

      {stations.length === 0 ? (
        <EmptyState title={t("common:states.empty")} />
      ) : (
        <section className="overflow-hidden rounded-md border bg-panel">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1060px] border-collapse text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t("app")}</th>
                  <th className="px-3 py-2">{t("client")}</th>
                  <th className="px-3 py-2">{t("hostname")}</th>
                  <th className="px-3 py-2">{t("status")}</th>
                  <th className="px-3 py-2">{t("version")}</th>
                  <th className="px-3 py-2">{t("platform")}</th>
                  <th className="px-3 py-2">{t("lastSeen")}</th>
                  <th className="px-3 py-2 text-right">{t("common:actions.open")}</th>
                </tr>
              </thead>
              <tbody>
                {stations.map((station) => (
                  <StationRow key={station.id} station={station} language={i18n.language} />
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            hasNext={stations.length === pageSize}
            onPage={(nextPage) => void navigate({ search: { ...search, page: nextPage > 1 ? String(nextPage) : undefined } })}
            onPageSize={(nextPageSize) => void navigate({ search: { ...search, pageSize: String(nextPageSize), page: undefined } })}
          />
        </section>
      )}
    </div>
  );
}

function stationParams(search: StationSearch) {
  const page = pageFromSearch(search.page);
  const pageSize = pageSizeFromSearch(search.pageSize);
  const params = new URLSearchParams({ limit: String(pageSize), offset: String((page - 1) * pageSize) });
  if (search.appId) {
    params.set("appId", search.appId);
  }
  if (search.clientId) {
    params.set("clientId", String(search.clientId));
  }
  if (search.status) {
    params.set("status", search.status);
  }
  if (search.activeOnly === false) {
    params.set("activeOnly", "false");
  }
  if (search.outdatedOnly === true) {
    params.set("outdatedOnly", "true");
  }
  if (search.sort) {
    params.set("sort", search.sort);
  }
  return params;
}

function Pagination({
  page,
  pageSize,
  hasNext,
  onPage,
  onPageSize
}: {
  page: number;
  pageSize: number;
  hasNext: boolean;
  onPage: (page: number) => void;
  onPageSize: (pageSize: number) => void;
}) {
  const { t } = useTranslation(["stations", "common"]);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">{t("pageSize")}</span>
        {[25, 50, 100].map((size) => (
          <Button key={size} size="sm" variant={pageSize === size ? "default" : "outline"} onClick={() => onPageSize(size)}>
            {size}
          </Button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft className="h-4 w-4" />
          {t("previous")}
        </Button>
        <span className="min-w-16 text-center text-muted-foreground">{t("page")} {page}</span>
        <Button size="sm" variant="outline" disabled={!hasNext} onClick={() => onPage(page + 1)}>
          {t("next")}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function pageFromSearch(value: string | undefined) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

function pageSizeFromSearch(value: string | undefined) {
  const parsed = Number(value);
  return [25, 50, 100].includes(parsed) ? parsed : 50;
}

function StationRow({ station, language }: { station: StationRecord; language: string }) {
  const { t } = useTranslation(["stations", "common"]);
  const stale = isOlderThanHours(station.lastSeenAt, 24);
  return (
    <tr className="border-t">
      <td className="px-3 py-2 font-mono text-xs">{station.appId}</td>
      <td className="px-3 py-2"><ClientIdentity clientId={station.clientId} /></td>
      <td className="max-w-56 truncate px-3 py-2">{station.hostname || "—"}</td>
      <td className="px-3 py-2">
        <Badge variant={station.status === "HEALTHY" ? "success" : station.status === "WARNING" ? "warning" : "destructive"}>
          {station.status}
        </Badge>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span>{station.currentVersion}</span>
          {station.outdated ? <Badge variant="warning">{t("outdated")}</Badge> : null}
        </div>
        {station.minSupportedVersion ? <div className="text-xs text-muted-foreground">{t("minSupported")} {station.minSupportedVersion}</div> : null}
      </td>
      <td className="px-3 py-2">{station.os}/{station.arch}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span>{relativeDateTime(station.lastSeenAt, language)}</span>
          {stale ? <Badge variant="warning">{t("stale")}</Badge> : null}
        </div>
        <div className="text-xs text-muted-foreground">{formatDateTime(station.lastSeenAt, language)}</div>
      </td>
      <td className="px-3 py-2 text-right">
        <Button asChild size="sm" variant="outline">
          <Link to="/stations/$stationId" params={{ stationId: station.id }} search={{ recoveryRequestId: undefined }}>
            <ExternalLink className="h-4 w-4" />
            {t("common:actions.open")}
          </Link>
        </Button>
      </td>
    </tr>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label className="flex items-center gap-1 text-xs text-muted-foreground">
        <Filter className="h-3 w-3" />
        {label}
      </Label>
      {children}
    </div>
  );
}
